import { Eraser, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import type { Msg } from '../lib/socket';

/** Pen colours, matching the server's WHITEBOARD_COLORS. */
const WB_COLORS = ['#111827', '#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#a855f7'];

type Pt = { x: number; y: number };
type Stroke = { points: Pt[]; color: string; width: number; erase?: boolean };

/**
 * A shared blank whiteboard. Draws locally and sends each finished stroke (points in 0..1 of the
 * canvas) via `send`; renders strokes and clears that arrive through `subscribe`. Used by both the
 * parent (dashboard) and the child (agent page), wired to their own socket.
 */
export function Whiteboard({
  send,
  subscribe,
  label,
}: {
  send: (payload: object) => void;
  subscribe: (fn: (m: Msg) => void) => () => void;
  label?: string;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [color, setColor] = useState(WB_COLORS[0]!);
  const [width, setWidth] = useState(3);
  const [erase, setErase] = useState(false);
  const strokes = useRef<Stroke[]>([]);
  const drawing = useRef<Stroke | null>(null);
  // Latest tool choice, so the pointer handlers (bound once) always use the current colour/width.
  const tool = useRef({ color, width, erase });
  tool.current = { color, width, erase };

  function paint(ctx: CanvasRenderingContext2D, s: Stroke, w: number, h: number) {
    if (!s.points.length) return;
    ctx.globalCompositeOperation = s.erase ? 'destination-out' : 'source-over';
    ctx.strokeStyle = s.color;
    ctx.lineWidth = s.width * (s.erase ? 3 : 1);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    s.points.forEach((p, i) => {
      const x = p.x * w;
      const y = p.y * h;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    if (s.points.length === 1) ctx.lineTo(s.points[0]!.x * w + 0.01, s.points[0]!.y * h); // a dot
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }

  function redraw() {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    for (const s of strokes.current) paint(ctx, s, c.width, c.height);
    if (drawing.current) paint(ctx, drawing.current, c.width, c.height);
  }

  // Keep the canvas sized to its box (and redraw when it changes).
  useEffect(() => {
    const resize = () => {
      const c = canvasRef.current;
      const box = wrapRef.current;
      if (!c || !box) return;
      c.width = Math.max(1, box.clientWidth);
      c.height = Math.max(1, box.clientHeight);
      redraw();
    };
    resize();
    const ro = new ResizeObserver(resize);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);

  // Strokes and clears from the other side.
  useEffect(
    () =>
      subscribe((m) => {
        if (m.clear === true) {
          strokes.current = [];
          redraw();
        } else if (m.stroke && typeof m.stroke === 'object') {
          strokes.current.push(m.stroke as Stroke);
          redraw();
        }
      }),
    [subscribe],
  );

  const norm = (e: ReactPointerEvent<HTMLCanvasElement>): Pt => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };
  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = { points: [norm(e)], color: tool.current.color, width: tool.current.width, erase: tool.current.erase };
    redraw();
  };
  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current.points.push(norm(e));
    redraw();
  };
  const onUp = () => {
    const s = drawing.current;
    drawing.current = null;
    if (!s) return;
    strokes.current.push(s);
    send({ stroke: s });
    redraw();
  };
  const clear = () => {
    strokes.current = [];
    redraw();
    send({ clear: true });
  };

  return (
    <div className="whiteboard">
      <div className="whiteboard-tools">
        {WB_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={`wb-color ${color === c && !erase ? 'active' : ''}`}
            style={{ background: c }}
            aria-label={`Pen colour ${c}`}
            onClick={() => {
              setColor(c);
              setErase(false);
            }}
          />
        ))}
        <button type="button" className={`wb-tool ${erase ? 'active' : ''}`} aria-pressed={erase} onClick={() => setErase((v) => !v)}>
          <Eraser size={16} /> Eraser
        </button>
        <input type="range" min={1} max={16} value={width} onChange={(e) => setWidth(Number(e.target.value))} aria-label="Pen width" className="wb-width" />
        <button type="button" className="wb-tool" onClick={clear}>
          <Trash2 size={16} /> Clear
        </button>
      </div>
      <div className="whiteboard-canvas-wrap" ref={wrapRef}>
        <canvas ref={canvasRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} />
      </div>
      {label && <p className="muted small center">{label}</p>}
    </div>
  );
}
